import { isExcludedTerm } from './termSides';

/**
 * Related terms that were ON by default (in the per-term top-N snapshot `initiallySelectedSynonyms`)
 * but are NOT in the final submitted selection — i.e. the user removed them, split by the side the
 * parent term sits on so the backend records inclusion vs exclusion (SCIL-16099).
 *
 * Net-state, not event-based: it compares the default against the final selection, so it captures
 * a removal however the user got there — one chip at a time, or a bulk "unselect all" followed by
 * re-selecting a subset (that scenario leaves the un-reselected defaults correctly counted).
 *
 * Guards / consequences:
 *  - `synonymsEnabled` false (the feature is off / nothing is selected) -> record nothing, so a
 *    plain "toggle everything off" is not logged.
 *  - a term whose row is disabled (`termEnabled[term] === false`) is skipped.
 *  - beyond-top-N and shared-off chips are never in `initiallySelectedSynonyms`, so they can never
 *    appear here — turning one on then off is not a deselection.
 *
 * @returns {{ include: Record<string, string[]>, exclude: Record<string, string[]> }}
 */
export function buildDeselectionPayload({
  effectiveSynonyms,
  termEnabled,
  synonymsEnabled,
  termSideMap,
  initiallySelectedSynonyms,
}) {
  if (!synonymsEnabled) return { include: {}, exclude: {} };
  const enabled = termEnabled || {};
  const effective = effectiveSynonyms || {};
  const initial = initiallySelectedSynonyms || {};

  const include = {};
  const exclude = {};
  for (const [term, initialSyns] of Object.entries(initial)) {
    if (enabled[term] === false) continue;
    const stillSelected = new Set(
      (effective[term] || []).map(s => s.toLowerCase())
    );
    // A default-on chip that is no longer selected was removed by the user.
    const removed = (initialSyns || []).filter(
      s => !stillSelected.has(s.toLowerCase())
    );
    if (removed.length > 0) {
      const target = isExcludedTerm(termSideMap, term) ? exclude : include;
      target[term] = removed;
    }
  }
  return { include, exclude };
}
